import React, { createContext, ReactNode, useContext } from 'react';

import { InteractionService } from './interaction-service';

export interface InteractionContextValue {
	interactionService: InteractionService;
}

const InteractionContext = createContext<InteractionContextValue | null>(null);

interface InteractionProviderProps {
	interactionService: InteractionService;
	children: ReactNode;
}

/**
 * InteractionProvider component that provides InteractionService to all child components
 * This eliminates the need to pass interactionService as props through multiple levels
 */
export const InteractionProvider: React.FC<InteractionProviderProps> = ({
	interactionService,
	children
}) => {
	const contextValue: InteractionContextValue = {
		interactionService
	};

	return (
		<InteractionContext.Provider value={contextValue}>
			{children}
		</InteractionContext.Provider>
	);
};

/**
 * Hook to access the interaction service from context
 * Must be used within an InteractionProvider
 */
export const useInteractionContext = (): InteractionContextValue => {
	const context = useContext(InteractionContext);
	if (!context) {
		throw new Error('useInteractionContext must be used within an InteractionProvider');
	}
	return context;
};

/**
 * Hook to access the interaction service directly
 * Convenience hook that extracts interactionService from context
 */
export const useInteractionService = (): InteractionService => {
	const { interactionService } = useInteractionContext();
	return interactionService;
};

export default InteractionProvider;
